import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as PriceIcon,
  Assessment as AnalyticsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Timeline as TimelineIcon,
  Star as StarIcon,
  ThumbUp as AdvantageIcon,
  ThumbDown as DisadvantageIcon,
} from '@mui/icons-material';
import { PricingAnalysisResult } from '../../../types/property';

interface PricingTabProps {
  data: PricingAnalysisResult;
  loading?: boolean;
  error?: string;
}

const PricingTab: React.FC<PricingTabProps> = ({ data, loading, error }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading pricing analysis...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 3 }}>
        No pricing analysis available
      </Alert>
    );
  }

  const { marketPositioning, pricingStrategy, competitiveAnalysis, marketTiming, recommendations, riskAssessment } = data;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const getRiskColor = (risk: number) => {
    if (risk < 0.3) return 'success';
    if (risk < 0.6) return 'warning';
    return 'error';
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Pricing Strategy Overview */}
        <Box>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PriceIcon color="primary" />
                  Pricing Strategy
                </Typography>
                <Chip
                  icon={<CheckIcon />}
                  label={`${Math.round(data.confidence * 100)}% Confidence`}
                  color={getConfidenceColor(data.confidence)}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1, textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2, color: 'success.contrastText' }}>
                  <Typography variant="h4" fontWeight="bold">
                    {formatPrice(pricingStrategy.recommendedPrice)}
                  </Typography>
                  <Typography variant="subtitle1">
                    Recommended Price
                  </Typography>
                </Box>

                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Approach
                  </Typography>
                  <Chip 
                    label={pricingStrategy.pricingApproach} 
                    color="primary" 
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>

                <Box sx={{ flex: 1, textAlign: 'center', p: 2 }}>
                  <Typography variant="h6" color="text.secondary">Confidence</Typography>
                  <Typography variant="h5" color="primary">{Math.round(pricingStrategy.pricingConfidence * 100)}%</Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Price Justification
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Based on market comparables, property features, and location advantages
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Market Analysis & Risk Assessment */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {/* Market Timing */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon color="primary" />
                  Market Timing
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Market Cycle</Typography>
                  <Chip 
                    label={marketTiming.marketCycle} 
                    color={marketTiming.marketCycle === 'favorable' ? 'success' : marketTiming.marketCycle === 'challenging' ? 'error' : 'warning'}
                    size="small"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Optimal Listing Time</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {marketTiming.optimalListingTime}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Seasonal Factors</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {Object.keys(marketTiming.seasonalFactors).length} seasonal factors analyzed
                  </Typography>
                </Box>

                {marketTiming.timingRecommendations && marketTiming.timingRecommendations.length > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Timing Recommendations
                    </Typography>
                    <List dense>
                      {marketTiming.timingRecommendations.map((rec, index) => (
                        <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText 
                            primary={rec} 
                            slotProps={{ primary: { variant: 'body2' } }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Market Positioning */}
          <Box sx={{ flex: 1 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnalyticsIcon color="primary" />
                  Market Position
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 200px', textAlign: 'center', p: 1 }}>
                    <Typography variant="body2" color="text.secondary">Price Segment</Typography>
                    <Typography variant="h6">
                      {marketPositioning.priceSegment}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: '1 1 200px', textAlign: 'center', p: 1 }}>
                    <Typography variant="body2" color="text.secondary">Competitive Position</Typography>
                    <Chip 
                      label={marketPositioning.competitivePosition} 
                      color={marketPositioning.competitivePosition === 'high' ? 'error' : marketPositioning.competitivePosition === 'low' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Competitive Analysis */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <AnalyticsIcon color="primary" />
                Competitive Analysis
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'success.main' }}>
                    Competitive Advantages
                  </Typography>
                  <List dense>
                    {competitiveAnalysis.competitiveAdvantages.map((advantage: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <AdvantageIcon fontSize="small" color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={advantage} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'warning.main' }}>
                    Competitive Disadvantages
                  </Typography>
                  <List dense>
                    {competitiveAnalysis.competitiveDisadvantages.map((disadvantage: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <DisadvantageIcon fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={disadvantage} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'info.main' }}>
                    Positioning Opportunities
                  </Typography>
                  <List dense>
                    {competitiveAnalysis.positioningOpportunities.map((opportunity: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <StarIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={opportunity} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Recommendations */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckIcon color="primary" />
                Pricing Recommendations
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'primary.main' }}>
                    Immediate Actions
                  </Typography>
                  <List dense>
                    {recommendations.immediate.map((action: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <PriceIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={action} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: 'secondary.main' }}>
                    Short Term Actions
                  </Typography>
                  <List dense>
                    {recommendations.shortTerm.map((action: string, index: number) => (
                      <ListItem key={index} sx={{ py: 0.25, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <TrendingUpIcon fontSize="small" color="secondary" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={action} 
                          slotProps={{ primary: { variant: 'body2' } }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Risk Assessment */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="primary" />
                Risk Assessment
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                    <Typography variant="h5" fontWeight="bold" color="info.contrastText">
                      {Math.round(riskAssessment.overpricing * 100)}%
                    </Typography>
                    <Typography variant="subtitle2" color="info.contrastText">
                      Overpricing Risk
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 2 }}>
                    <Typography variant="h5" fontWeight="bold" color="warning.contrastText">
                      {Math.round(riskAssessment.marketRisk * 100)}%
                    </Typography>
                    <Typography variant="subtitle2" color="warning.contrastText">
                      Market Risk
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                    <Typography variant="h5" fontWeight="bold" color="error.contrastText">
                      {Math.round(riskAssessment.timingRisk * 100)}%
                    </Typography>
                    <Typography variant="subtitle2" color="error.contrastText">
                      Timing Risk
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default PricingTab;